import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Boxes, Loader2, Lock, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@wms.com");
  const [password, setPassword] = useState("Admin@123");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* left form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md fade-up">
          <div className="flex items-center gap-2.5 mb-8">
            <div className="h-11 w-11 rounded-lg bg-blue-600 flex items-center justify-center">
              <Boxes className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-xl text-slate-900">StockPilot</div>
              <div className="text-[11px] uppercase tracking-wider text-slate-400">Warehouse Management OS</div>
            </div>
          </div>

          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Sign in</h1>
          <p className="text-slate-500 mt-1.5 mb-8">Enterprise warehouse operations, unified.</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="mb-1.5 block">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="pl-9 h-12" placeholder="you@company.com" required />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label>Password</Label>
                <button type="button" className="text-xs text-blue-600 hover:underline"
                  onClick={() => toast.info("Reset link logged to server console (demo).")}>Forgot password?</button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 h-12" placeholder="••••••••" required />
              </div>
            </div>

            {error && <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}

            <Button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-base font-semibold group">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-0.5 transition-transform" /></>}
            </Button>
          </form>

          <div className="mt-8 p-4 rounded-lg bg-white border border-slate-200 text-xs text-slate-500">
            <div className="font-semibold text-slate-700 mb-1.5">Demo accounts</div>
            <div className="font-mono space-y-0.5">
              <div>admin@wms.com · Admin@123</div>
              <div>manager@wms.com · Password@123</div>
              <div>keeper@wms.com · Password@123</div>
            </div>
          </div>
        </div>
      </div>

      {/* right hero */}
      <div className="hidden lg:block flex-1 relative overflow-hidden">
        <img src="https://images.pexels.com/photos/14554082/pexels-photo-14554082.jpeg"
          alt="Warehouse" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-blue-900/70" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <h2 className="font-display text-4xl font-bold leading-tight max-w-md">Run your warehouse like a Fortune 500.</h2>
          <p className="text-blue-100 mt-4 max-w-md text-lg">Real-time inventory, RFID/QR scanning, put-away automation, wave picking, and complete audit trails — in one platform.</p>
          <div className="flex gap-8 mt-10">
            {[["25+", "Modules"], ["RBAC", "9 Roles"], ["100%", "Auditable"]].map(([n, l]) => (
              <div key={l}><div className="font-display text-3xl font-bold">{n}</div><div className="text-blue-200 text-sm">{l}</div></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
